import { useCallback, useMemo, useState } from 'react'

import { Map } from '@vis.gl/react-google-maps'
import { useSearchParams } from 'react-router'

import type { TGetBlocosResponse } from '@/features/blocos/interfaces/bloco.types'
import type { TLote } from '@/features/produtos/interfaces/produto.types'
import type { Coordinate, ImageOverlayProps, Region } from '@/shared/interfaces/mapa.types'
import { cn } from '@/shared/lib'

import { MapContent } from './map-content'

const MIN_ZOOM_LEVEL = 20
const MAX_ZOOM_LEVEL = 22

export interface GoogleMapWithImageOverlayProps {
  className?: string
  overlayCoordinates: [Coordinate, Coordinate]
  image: ImageOverlayProps
  lotes?: TLote[]
  quadras: TGetBlocosResponse
  minZoomLevel?: number
  maxZoomLevel?: number
  onRegionChange?: (region: Region) => void
  onMapLoaded?: () => void
  onLotePress?: (lote: TLote) => void
  baseLatLngDelta: number
  initialRegion: Region
}

export const GoogleMapWithImageOverlay = ({
  className,
  overlayCoordinates,
  image,
  lotes = [],
  quadras,
  baseLatLngDelta,
  initialRegion,
  minZoomLevel = MIN_ZOOM_LEVEL,
  maxZoomLevel = MAX_ZOOM_LEVEL,
  onRegionChange,
  onMapLoaded,
  onLotePress,
}: GoogleMapWithImageOverlayProps) => {
  const [searchParams] = useSearchParams()
  const [currentZoom, setCurrentZoom] = useState(minZoomLevel)

  const handleCameraChange = useCallback(
    (event: { detail: { zoom?: number; center?: { lat: number; lng: number } } }) => {
      const newZoom = event.detail.zoom ?? currentZoom
      setCurrentZoom(newZoom)

      if (onRegionChange && event.detail.center) {
        const center = event.detail.center
        onRegionChange({
          latitude: center.lat,
          longitude: center.lng,
          latitudeDelta: baseLatLngDelta,
          longitudeDelta: baseLatLngDelta,
        })
      }
    },
    [currentZoom, onRegionChange],
  )

  const initialCenter = useMemo(() => {
    const quadraParam = searchParams.get('quadra')
    if (quadraParam) {
      const quadraIdExterno = parseInt(quadraParam, 10)
      const foundQuadra = quadras.find((quadra) => quadra.idExterno === quadraIdExterno)
      if (foundQuadra) {
        return {
          lat: foundQuadra.coordenada.latitude,
          lng: foundQuadra.coordenada.longitude,
        }
      }
    }
    return {
      lat: initialRegion.latitude,
      lng: initialRegion?.longitude,
    }
  }, [searchParams, quadras])

  const restrictionBounds = useMemo(() => {
    const [northWest, southEast] = overlayCoordinates
    return {
      north: northWest.latitude,
      south: southEast.latitude,
      east: southEast.longitude,
      west: northWest.longitude,
    }
  }, [overlayCoordinates])

  return (
    <Map
      mapId="ESPELHO_VENDAS_HORIZONTAL"
      className={cn('relative z-30 min-h-full w-full rounded border', className)}
      defaultCenter={initialCenter}
      defaultZoom={minZoomLevel}
      minZoom={minZoomLevel}
      maxZoom={maxZoomLevel}
      restriction={{
        latLngBounds: restrictionBounds,
        strictBounds: true,
      }}
      gestureHandling="cooperative"
      disableDefaultUI={true}
      zoomControl={true}
      scrollwheel={true}
      disableDoubleClickZoom={false}
      clickableIcons={false}
      streetViewControl={false}
      fullscreenControl={false}
      mapTypeControl={false}
      rotateControl={false}
      scaleControl={false}
      keyboardShortcuts={false}
      onCameraChanged={handleCameraChange}
      onTilesLoaded={() => {
        if (onMapLoaded) {
          onMapLoaded()
        }
      }}
    >
      <MapContent
        overlayCoordinates={overlayCoordinates}
        imageUri={image.uri}
        lotes={lotes}
        minZoomLevel={minZoomLevel}
        maxZoomLevel={maxZoomLevel}
        onLotePress={onLotePress}
      />
    </Map>
  )
}
